import { Link } from "react-router";
import { DEMO, publicApiOrigin } from "../config";
import { useAgentsCreatedBy, useSystemPrompt } from "../lib/queries";
import { AgentCard } from "../components/AgentCard";
import { Empty, SkeletonLines } from "../components/ui";
import { bytes, shortAddr } from "../lib/format";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { isCanonicalAgentId, runtimeStatusIsDisplayFresh, WalletAuthorizationError, type PrivateAgentStatus } from "../lib/wallet-auth";
import { PUBLIC_V2_CONFIG } from "../lib/public-v2";
import { useWalletSession } from "../lib/use-wallet-session";
import { useSwitchChain } from "wagmi";
import { base } from "wagmi/chains";

export function GenesisPage() {
  const query = useSystemPrompt();
  return <>
    <span className="kicker">Genesis</span><h1 style={{ marginTop: 18 }}>System prompt</h1>
    <p className="lede">A public V2 system-prompt record, when configured and validated.</p>
    {query.state === "loading" ? <SkeletonLines lines={5} /> : query.state === "data" && query.data
      ? <div className="panel panel-pad"><div className="spread"><span className="label">Finalized on-chain</span><span className="tiny muted">{bytes(query.data.decodedBytes)} decoded</span></div>
        {query.data.decodeError ? <div className="notice">Decode error: {query.data.decodeError}</div>
          : query.data.chunkCount === 0 && query.data.text === "" && query.data.decodedBytes === 0
            ? <div className="notice">Validated empty system prompt</div>
            : <pre style={{ whiteSpace: "pre-wrap", marginTop: 16 }}>{query.data.text ?? query.data.displayText ?? "Text unavailable"}</pre>}
        <dl className="kv"><dt>system prompt hash</dt><dd className="hash">{query.data.systemPromptHash}</dd><dt>Public API</dt><dd>{publicApiOrigin ? `${publicApiOrigin}/v2/system-prompt` : "unavailable"}</dd></dl></div>
      : <Empty>{query.state === "unavailable" ? "Public V2 data is not configured." : query.state === "not-found" ? "No finalized system prompt is available." : "System prompt unavailable."}</Empty>}
  </>;
}

export function CreatorPurchasesView({ address }: { address: `0x${string}` | undefined }) {
  const query = useAgentsCreatedBy(address);
  if (!address) return <Empty>Connect a wallet to filter public purchases by creator. No wallet permissions or private reads are implied.</Empty>;
  return <>
    <div className="kicker">Creator {shortAddr(address)}</div><h1 style={{ marginTop: 18 }}>Purchases created by you</h1>
    {query.state === "loading" ? <SkeletonLines /> : query.state === "unavailable" ? <Empty>Public V2 data is not configured.</Empty>
        : query.state === "failed" ? <Empty>Creator-filtered data is unavailable.</Empty>
        : query.purchases.length === 0 ? <Empty>No public purchases match this creator address.</Empty>
          : <div className="grid-cards">{query.purchases.map((purchase) => <AgentCard key={purchase.agentId} agent={purchase} />)}</div>}
    {query.hasMore && <div className="row" style={{ marginTop: 24 }}><button className="btn" disabled={"isFetchingMore" in query ? query.isFetchingMore : false} onClick={() => void query.loadMore()}>Load more</button></div>}
    {(query.hasMore || query.hasRetainedPageLimit) && <p className="small muted" style={{ marginTop: 12 }}>At most four pages are retained; older loaded records may have been evicted.</p>}
  </>;
}

export function WalletPrivateStatus() {
  const wallet = useWalletSession({
    deployment: PUBLIC_V2_CONFIG.kind === "configured" ? PUBLIC_V2_CONFIG.reference : null,
    access: "private_reads",
  });
  const { address, chainId, status } = wallet.connection;
  const { switchChain, isPending: switching } = useSwitchChain();
  const [agentId, setAgentId] = useState("");
  const [privateResult, setPrivateResult] = useState<{ status: PrivateAgentStatus; operation: ReturnType<typeof wallet.beginOperation>;
    agentId: string; requestStartedMonotonicMs: number; responseReceivedMonotonicMs: number } | null>(null);
  const [privateError, setPrivateError] = useState<{ operation: ReturnType<typeof wallet.beginOperation>; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [, setFreshnessTick] = useState(0);
  const deployment = PUBLIC_V2_CONFIG.kind === "configured" ? PUBLIC_V2_CONFIG.reference : null;
  const deploymentKey = JSON.stringify(deployment);
  const authorized = !!deployment && wallet.capabilities?.walletAuthentication === true && wallet.capabilities.privateAgentReads === true;
  const session = wallet.session;
  const sessionValid = !!session && Math.min(session.expiresAtMs, session.idleExpiresAtMs) > Date.now();
  const identityKey = JSON.stringify({ status, address: address?.toLowerCase() ?? null, chainId: chainId ?? null,
    deploymentKey, authorized, sessionValid });
  const previousIdentityKey = useRef(identityKey);
  const privateResultRef = useRef(privateResult);
  privateResultRef.current = privateResult;
  const busyOperation = useRef<ReturnType<typeof wallet.beginOperation> | null>(null);
  const visibleBusy = busy && (!busyOperation.current || busyOperation.current.isCurrent());
  const error = wallet.error;

  const clearPrivateView = () => {
    privateResultRef.current = null;
    setPrivateResult(null);
    setPrivateError(null);
    setBusy(false);
    busyOperation.current = null;
  };
  useEffect(() => {
    if (previousIdentityKey.current === identityKey) return;
    previousIdentityKey.current = identityKey;
    clearPrivateView();
    setBusy(false);
  }, [identityKey]);

  const readStatus = async (event: FormEvent) => {
    event.preventDefault();
    if (!session || !authorized || !deployment || !address || chainId !== 8453 || session.address.toLowerCase() !== address.toLowerCase() ||
        Math.min(session.expiresAtMs, session.idleExpiresAtMs) <= Date.now() || !isCanonicalAgentId(agentId)) return;
    const operation = wallet.beginOperation();
    busyOperation.current = operation;
    setBusy(true);
    setPrivateResult(null);
    setPrivateError(null);
    privateResultRef.current = null;
    try {
      const requestStartedMonotonicMs = performance.now();
      const value = await wallet.client.privateAgentStatus(deployment.registryAddress, agentId, session.csrfToken, operation.signal);
      if (!operation.isCurrent()) return;
      const responseReceivedMonotonicMs = performance.now();
      const result = { status: value, operation, agentId, requestStartedMonotonicMs, responseReceivedMonotonicMs };
      privateResultRef.current = result;
      setPrivateResult(result);
      await wallet.refreshSession(operation);
    } catch (cause) {
      if (operation.isCurrent()) {
        if (cause instanceof WalletAuthorizationError && (cause.code === "unauthorized" || cause.code === "expired" || cause.code === "invalid_signature")) {
          clearPrivateView();
          wallet.clearSession();
        } else {
          clearPrivateView();
          setPrivateError({ operation, message: cause instanceof WalletAuthorizationError ? cause.message : "Private hosting status is unavailable." });
        }
      }
    } finally {
      if (operation.isCurrent()) {
        setBusy(false);
        busyOperation.current = null;
      }
      operation.finish();
    }
  };

  const currentPrivateResult = privateResultRef.current;
  const visiblePrivateStatus = session && currentPrivateResult?.operation.isCurrent() && currentPrivateResult.agentId === agentId
    ? currentPrivateResult : null;
  const displayFreshnessDeadline = visiblePrivateStatus?.status.runtimeStatus.freshness === "fresh" &&
      visiblePrivateStatus.status.runtimeStatus.statusValidUntilMs !== null
    ? visiblePrivateStatus.requestStartedMonotonicMs +
      Math.max(0, visiblePrivateStatus.status.runtimeStatus.statusValidUntilMs - visiblePrivateStatus.status.runtimeStatus.serverNowMs)
    : null;
  useEffect(() => {
    if (displayFreshnessDeadline === null) return;
    let timer: ReturnType<typeof setTimeout>;
    const checkFreshness = () => {
      const remainingMs = displayFreshnessDeadline - performance.now();
      if (remainingMs <= 0) {
        setFreshnessTick(value => value + 1);
        return;
      }
      timer = setTimeout(checkFreshness, Math.max(1, Math.ceil(remainingMs)));
    };
    timer = setTimeout(checkFreshness, Math.max(1, Math.ceil(displayFreshnessDeadline - performance.now())));
    return () => clearTimeout(timer);
  }, [displayFreshnessDeadline]);

  if (status !== "connected" || !address) return <Empty>Connect a wallet to sign in and view private hosting metadata. Public purchase records do not grant private access.</Empty>;
  if (chainId !== 8453) return <div className="stack"><h1>Private hosting</h1><p className="lede">Switch to Base before signing in.</p><button className="btn" disabled={switching} onClick={() => switchChain({ chainId: base.id })}>{switching ? "Switching…" : "Switch to Base"}</button></div>;
  if (!deployment || !wallet.capabilities || !authorized) return <Empty>Private wallet access is unavailable until the public and control services advertise the same enabled Base deployment.</Empty>;

  const runtimeFreshness = visiblePrivateStatus
    ? runtimeStatusIsDisplayFresh(visiblePrivateStatus.status.runtimeStatus, visiblePrivateStatus.requestStartedMonotonicMs,
        visiblePrivateStatus.responseReceivedMonotonicMs, performance.now())
      ? "fresh"
      : visiblePrivateStatus.status.runtimeStatus.freshness === "fresh" ? "unverified" : visiblePrivateStatus.status.runtimeStatus.freshness
    : null;

  return <div className="stack">
    <span className="kicker">Private hosting · {shortAddr(address)}</span><h1 style={{ marginTop: 0 }}>Hosting status</h1>
    <p className="lede">Sign in with the current owner wallet to read bounded hosting metadata. This does not verify that a runtime is healthy or enable launch or control actions.</p>
    {!session ? <button className="btn" disabled={visibleBusy || wallet.signingIn || wallet.logoutPending} onClick={() => { clearPrivateView(); void wallet.signIn(); }}>{wallet.logoutPending ? "Signing out…" : visibleBusy || wallet.signingIn ? "Signing in…" : "Sign in with wallet"}</button> : <>
      <div className="panel panel-pad"><span className="label">Wallet session active</span><p className="small muted">Session expires {new Date(session.expiresAtMs).toLocaleString()}.</p><button className="linkish" onClick={() => { clearPrivateView(); void wallet.signOut(); }}>Sign out</button></div>
      <form className="panel panel-pad stack" onSubmit={readStatus}>
        <label className="stack"><span className="label">Registry V2 agent ID</span><input inputMode="numeric" pattern="0|[1-9][0-9]*" value={agentId} onChange={event => setAgentId(event.target.value)} maxLength={78} required /></label>
        <button className="btn" disabled={visibleBusy || !isCanonicalAgentId(agentId)}>{visibleBusy ? "Reading…" : "Read private status"}</button>
      </form>
    </>}
    {error && <div className="notice" role="alert">{error}</div>}
    {privateError?.operation.isCurrent() && <div className="notice" role="alert">{privateError.message}</div>}
    {visiblePrivateStatus && <>
      <section className="panel panel-pad"><span className="label">Hosting metadata · {visiblePrivateStatus.status.runtime.state}</span><dl className="kv">
        <dt>agent</dt><dd>{visiblePrivateStatus.status.agentId}</dd><dt>account</dt><dd>{shortAddr(visiblePrivateStatus.status.accountAddress)}</dd>
        <dt>created</dt><dd>{visiblePrivateStatus.status.runtime.createdAtMs === null ? "not recorded" : new Date(visiblePrivateStatus.status.runtime.createdAtMs).toLocaleString()}</dd>
        <dt>started</dt><dd>{visiblePrivateStatus.status.runtime.startedAtMs === null ? "not started" : new Date(visiblePrivateStatus.status.runtime.startedAtMs).toLocaleString()}</dd>
        <dt>expires</dt><dd>{visiblePrivateStatus.status.runtime.expiresAtMs === null ? "not recorded" : new Date(visiblePrivateStatus.status.runtime.expiresAtMs).toLocaleString()}</dd>
        <dt>observed</dt><dd>{new Date(visiblePrivateStatus.status.observedAtMs).toLocaleString()}</dd>
      </dl><p className="small muted">This is server-side lifecycle metadata, not a verified health check.</p></section>
      <section className="panel panel-pad" aria-label="Runtime evidence"><span className="label">{runtimeFreshness === "fresh" ? "Runtime evidence" : "Last observed runtime"} · {visiblePrivateStatus.status.runtimeStatus.state}</span>
        <p className="small">Freshness · {runtimeFreshness}</p>
        <p className="small">Runtime expiry · {visiblePrivateStatus.status.runtimeStatus.expiresAtMs === null
          ? "not reported" : new Date(visiblePrivateStatus.status.runtimeStatus.expiresAtMs).toLocaleString()}</p>
        <p className="small">Cleanup · {visiblePrivateStatus.status.runtimeStatus.cleanup}</p>
        <p className="small muted">Runtime evidence is a bounded server projection, not a live health check.</p>
      </section>
    </>}
  </div>;
}

export function MePage() { return DEMO ? <Empty>Wallet sign-in and private hosting reads are disabled in the read-only preview.</Empty> : <WalletPrivateStatus />; }

export function DocsPage() {
  return <>
    <span className="kicker">Docs</span><h1 style={{ marginTop: 18 }}>Public Registry V2 records</h1>
    <p className="lede">This frontend validates bounded public API data against an explicit deployment reference. Public lifecycle data does not establish runtime health, private hosting access, wallet authorization or cash withdrawal.</p>
    <div className="panel panel-pad stack"><h3>Current availability</h3>
      <p>Purchase and prompt records may be read when a public V2 API and its matching deployment reference are configured.</p>
      <p>An enabled launch is expected to reserve a capacity slot, use a signed voucher, hold payment in contract escrow, and create an account that owns the identity. Launch and reservation remain unavailable here. Wallet sign-in now supports bounded private hosting metadata reads for the current account owner; it does not enable agent controls. Community reads and actions are also unavailable.</p>
      <p>Ready is an on-chain attestation, not a claim that a service is running. Refund and settlement credits do not mean funds have been withdrawn. Cleanup receipts and capacity-release flags are indexed attestations, not proof of physical purge or current quota.</p>
      <p>Genesis text is combined with a purchase only when both records carry the same system-prompt hash.</p>
      <p>A future launch flow may hold a reservation-backed voucher and payment in escrow while creating an account-bound identity. That flow is not available in this read-only frontend.</p>
    </div>
    <div className="section-head"><h2>Public endpoints</h2></div>
    <div className="panel panel-pad"><dl className="kv"><dt>deployment reference</dt><dd>{publicApiOrigin ? `${publicApiOrigin}/v2/deployment` : "unavailable"}</dd><dt>purchase list</dt><dd>{publicApiOrigin ? `${publicApiOrigin}/v2/purchases` : "unavailable"}</dd><dt>system prompt</dt><dd>{publicApiOrigin ? `${publicApiOrigin}/v2/system-prompt` : "unavailable"}</dd></dl><p><Link to="/">Explore public purchases</Link></p></div>
  </>;
}

export function NotFound() { return <Empty><div style={{ fontFamily: "var(--font-display)", fontSize: 40, color: "var(--ink)" }}>404</div>signal lost. <Link to="/">return to explore →</Link></Empty>; }
